import type { SourceSystem } from 'eds-common';
import type { AccessCodeRedemptionInitialState } from './access-code-deep-link';

export const STEP_IDS = {
  ENTER_CODE: 'enter-code',
  WELCOME_REDEMPTION: 'welcome-redemption',
  CONFIRM: 'confirm',
  AUTO_ENTITLE: 'auto-entitle',
  CONFIRM_ORG: 'confirm-org',
  SUCCESS_AUTO_ENTITLE: 'success-auto-entitle',
  SUCCESS_MYSCHOLASTIC: 'success-myscholastic',
} as const;

// User sources that auto-entitle classes rather than confirming an org.
const AUTO_ENTITLE_SOURCES = new Set<SourceSystem>([
  'Ednition',
  'GoogleClassroom',
]);

export type StepId = (typeof STEP_IDS)[keyof typeof STEP_IDS];

export type StepContext = {
  userSource: SourceSystem;
  isFirstTime: boolean;
};

export type StepConfig = {
  title: string;
  description?: string;
  hideStandardHeader?: boolean;
  // Returns the next step id, or null if this is a terminal step.
  next: (context: StepContext) => StepId | null;
};

const title = 'Product setup';

export const stepConfigs: Record<StepId, StepConfig> = {
  [STEP_IDS.ENTER_CODE]: {
    title,
    next: ({ isFirstTime }) =>
      isFirstTime ? STEP_IDS.WELCOME_REDEMPTION : STEP_IDS.CONFIRM,
  },
  [STEP_IDS.WELCOME_REDEMPTION]: {
    title: 'Welcome to the Scholastic Education Platform!',
    hideStandardHeader: true,
    next: ({ userSource }) =>
      AUTO_ENTITLE_SOURCES.has(userSource)
        ? STEP_IDS.AUTO_ENTITLE
        : STEP_IDS.CONFIRM_ORG,
  },
  [STEP_IDS.CONFIRM]: {
    title,
    next: ({ userSource }) =>
      AUTO_ENTITLE_SOURCES.has(userSource)
        ? STEP_IDS.AUTO_ENTITLE
        : STEP_IDS.CONFIRM_ORG,
  },
  [STEP_IDS.AUTO_ENTITLE]: {
    title,
    next: () => STEP_IDS.SUCCESS_AUTO_ENTITLE,
  },
  [STEP_IDS.CONFIRM_ORG]: {
    title,
    next: () => STEP_IDS.SUCCESS_MYSCHOLASTIC,
  },
  [STEP_IDS.SUCCESS_AUTO_ENTITLE]: {
    title: 'Setup complete!',
    hideStandardHeader: true,
    next: () => null,
  },
  [STEP_IDS.SUCCESS_MYSCHOLASTIC]: {
    title: 'Setup complete!',
    hideStandardHeader: true,
    next: () => null,
  },
};

export function getInitialStepId({
  initialState,
  isFirstTime,
}: {
  initialState: AccessCodeRedemptionInitialState | null | undefined;
  isFirstTime: boolean;
}): StepId {
  if (!initialState) {
    return STEP_IDS.ENTER_CODE;
  }
  // Deep-link arrivals already have a product in hand — skip ENTER_CODE and
  // land on the appropriate post-entry step.
  return isFirstTime ? STEP_IDS.WELCOME_REDEMPTION : STEP_IDS.CONFIRM;
}
